import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logo from '../components/Logo';
import * as api from '../services/api';
import loginFiber from '../assets/images/login-fiber.jpg';
import './Login.css';

const Login = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionExpiredMsg, setSessionExpiredMsg] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [twoFactorUserId, setTwoFactorUserId] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);

  const codeInputRefs = useRef([]);
  const navigate = useNavigate();
  const { login, refreshUser } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (searchParams.get('session_expired') === 'true') {
      setSessionExpiredMsg(true);
      window.history.replaceState({}, '', '/login');
    }
  }, [searchParams]);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    setLoading(false);

    if (result.success) {
      if (result.data.two_factor_required) {
        setTwoFactorUserId(result.data.two_factor_user_id);
        setShow2FA(true);
        setResendCooldown(60);
        return;
      }

      if (!result.data.email_verified) {
        navigate('/verify-email');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error || t('login.errors.failed'));
    }
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    const target = (forgotEmail || email).trim();
    if (!target) {
      setError(t('login.forgot.needEmail'));
      return;
    }
    setError('');
    setForgotEmail(target);
    setForgotSent(true);
  };

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...twoFactorCode];
    newCode[index] = value;
    setTwoFactorCode(newCode);

    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !twoFactorCode[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCodePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newCode = [...twoFactorCode];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setTwoFactorCode(newCode);
      const focusIndex = Math.min(pastedData.length, 5);
      codeInputRefs.current[focusIndex]?.focus();
    }
  };

  const handleVerify2FA = async () => {
    const code = twoFactorCode.join('');
    if (code.length !== 6) {
      setError(t('login.2fa.invalidCode'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api.verify2FA(twoFactorUserId, code);
      if (response.token) {
        await refreshUser();
        if (!response.email_verified) {
          navigate('/verify-email');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError(err.message || t('login.2fa.verifyError'));
      setTwoFactorCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResend2FA = async () => {
    if (resendCooldown > 0) return;

    setLoading(true);
    try {
      await api.resend2FA(twoFactorUserId);
      setResendCooldown(60);
      setError('');
      setTwoFactorCode(['', '', '', '', '', '']);
      codeInputRefs.current[0]?.focus();
    } catch (err) {
      setError(err.message || t('login.2fa.resendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setShow2FA(false);
    setShowForgot(false);
    setForgotSent(false);
    setTwoFactorUserId(null);
    setTwoFactorCode(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div className="arc-login">
      <aside className="arc-login-visual">
        <img src={loginFiber} alt="" className="arc-login-media" />
        <div className="arc-login-veil" aria-hidden="true" />
        <div className="arc-login-visual-copy">
          <button type="button" className="arc-login-brand" onClick={() => navigate('/')}>
            <Logo size="large" variant="full" className="arc-logo-on-dark" />
          </button>
          <h1>{t('login.branding.title')}</h1>
          <p>{t('login.branding.description')}</p>
        </div>
      </aside>

      <main className="arc-login-panel">
        <div className="arc-login-toolbar">
          <button type="button" className="arc-login-home" onClick={() => navigate('/')}>
            ArcVPN
          </button>
          <LanguageSwitcher variant="segmented" />
        </div>

        <div className="arc-login-card">
          {show2FA ? (
            <>
              <button type="button" className="arc-text-btn" onClick={handleBack}>
                ← {t('login.2fa.back')}
              </button>
              <header className="arc-login-header">
                <h2>{t('login.2fa.title')}</h2>
                <p>{t('login.2fa.description')}</p>
                <p className="arc-login-email">{email}</p>
              </header>

              {error && <div className="arc-alert arc-alert-error">{error}</div>}

              <div className="arc-otp">
                {twoFactorCode.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (codeInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    onPaste={handleCodePaste}
                    disabled={loading}
                    autoFocus={index === 0}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>

              <button
                type="button"
                className="arc-login-submit"
                onClick={handleVerify2FA}
                disabled={loading || twoFactorCode.join('').length !== 6}
              >
                {loading ? t('login.2fa.verifying') : t('login.2fa.verify')}
              </button>

              <div className="arc-login-resend">
                <span>{t('login.2fa.noCode')}</span>
                <button
                  type="button"
                  className="arc-text-btn"
                  onClick={handleResend2FA}
                  disabled={resendCooldown > 0 || loading}
                >
                  {resendCooldown > 0
                    ? t('login.2fa.resendIn', { seconds: resendCooldown })
                    : t('login.2fa.resend')}
                </button>
              </div>
            </>
          ) : showForgot ? (
            <>
              <button
                type="button"
                className="arc-text-btn"
                onClick={() => {
                  setShowForgot(false);
                  setForgotSent(false);
                  setError('');
                }}
              >
                ← {t('login.forgot.back')}
              </button>
              <header className="arc-login-header">
                <h2>{t('login.forgot.title')}</h2>
                <p>{t('login.forgot.description')}</p>
              </header>

              {forgotSent ? (
                <div className="arc-alert arc-alert-info">
                  {t('login.forgot.success', { email: forgotEmail })}
                </div>
              ) : (
                <form onSubmit={handleForgotSubmit} className="arc-login-form">
                  {error && <div className="arc-alert arc-alert-error">{error}</div>}
                  <label className="arc-field">
                    <span>{t('login.email')}</span>
                    <input
                      type="email"
                      value={forgotEmail || email}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder={t('login.emailPlaceholder')}
                      required
                      autoFocus
                    />
                  </label>
                  <button type="submit" className="arc-login-submit">
                    {t('login.forgot.submit')}
                  </button>
                </form>
              )}

              <p className="arc-login-footer">
                <button type="button" className="arc-text-btn" onClick={() => navigate('/contact')}>
                  {t('login.forgot.contact')}
                </button>
              </p>
            </>
          ) : (
            <>
              <header className="arc-login-header">
                <h2>{t('login.title')}</h2>
                <p>{t('login.subtitle')}</p>
              </header>

              <form onSubmit={handleSubmit} className="arc-login-form">
                {sessionExpiredMsg && (
                  <div className="arc-alert arc-alert-warn">{t('login.sessionExpired')}</div>
                )}
                {error && <div className="arc-alert arc-alert-error">{error}</div>}

                <label className="arc-field">
                  <span>{t('login.email')}</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.emailPlaceholder')}
                    required
                    disabled={loading}
                    autoComplete="email"
                  />
                </label>

                <label className="arc-field">
                  <span>{t('login.password')}</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.passwordPlaceholder')}
                    required
                    disabled={loading}
                    autoComplete="current-password"
                  />
                </label>

                <div className="arc-login-options">
                  <button
                    type="button"
                    className="arc-text-btn"
                    onClick={() => {
                      setShowForgot(true);
                      setForgotEmail(email);
                      setForgotSent(false);
                      setError('');
                    }}
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>

                <button type="submit" className="arc-login-submit" disabled={loading}>
                  {loading ? t('login.loading') : t('login.loginButton')}
                </button>
              </form>

              <p className="arc-login-footer">
                {t('login.noAccount')}{' '}
                <button type="button" className="arc-text-btn" onClick={() => navigate('/signup')}>
                  {t('login.signupLink')}
                </button>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Login;
